import React from 'react';
import { Input as ShadcnInput } from '@/components/ui/input';
import { Label as ShadcnLabel } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface InputFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'ref'> {
    label?: string;
    error?: string;
    description?: string;
}

export const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
    ({ className, label, error, description, id, ...props }, ref) => {
        // Generate a stable unique ID if not provided and we have a label
        const generatedId = React.useId();
        const inputId = id || (label ? generatedId : undefined);

        if (label) {
            return (
                <div className="flex flex-col space-y-2">
                    <ShadcnLabel htmlFor={inputId} className={cn(error && 'text-destructive')}>
                        {label}
                    </ShadcnLabel>
                    <ShadcnInput
                        ref={ref}
                        id={inputId}
                        className={cn(
                            // Glue's input styling
                            'h-8 px-2 rounded border border-border bg-background text-foreground',
                            'focus:ring-1 focus:ring-ring focus:border-ring',
                            error && 'border-destructive focus:border-destructive focus:ring-destructive',
                            className,
                        )}
                        {...props}
                    />
                    {error && <p className="text-sm text-destructive">{error}</p>}
                    {description && !error && <p className="text-sm text-muted-foreground">{description}</p>}
                </div>
            );
        }

        return (
            <ShadcnInput
                ref={ref}
                id={inputId}
                className={cn(
                    'h-8 px-2 rounded border border-border bg-background text-foreground',
                    'focus:ring-1 focus:ring-ring focus:border-ring',
                    error && 'border-destructive focus:border-destructive focus:ring-destructive',
                    className,
                )}
                {...props}
            />
        );
    },
);

InputField.displayName = 'InputField';
